import { type FC } from 'react';
import { products } from '@/mocks/products';

type Product = typeof products[0];

interface EditorPicksCarouselProps {
  products: Product[];
  onProductClick: (link: string) => void;
  getRetailerIcon: (retailer: string) => string;
  getRetailerName: (retailer: string) => string;
  isLimitedStock: (product: Product) => boolean;
}

const EditorPicksCarousel: FC<EditorPicksCarouselProps> = ({
  products: items,
  onProductClick,
  getRetailerIcon,
  getRetailerName,
  isLimitedStock,
}) => (
  <div className="relative">
    <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
      {items.map((product) => (
        <div
          key={product.id}
          className="flex-shrink-0 w-[260px] md:w-72 snap-start bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
          onClick={() => onProductClick(product.link)}
        >
          <div className="relative overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'https://public.readdy.ai/ai/img_res/3bef5e4cde10ebd7944becfba93f5226.jpg';
              }}
              className="w-full h-44 md:h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
            />
            {product.badge && (
              <div
                className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                  product.badge === 'SALE'
                    ? 'bg-rose-500 text-white'
                    : product.badge === 'BESTSELLER'
                    ? 'bg-amber-500 text-white'
                    : product.badge === 'TOP RATED'
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-700 text-white'
                }`}
              >
                {product.badge}
              </div>
            )}
            {isLimitedStock(product) && (
              <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-rose-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                Limited Stock
              </div>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1.5 mb-1.5">
              <i className="ri-medal-line text-amber-500 text-sm"></i>
              <span className="text-xs font-semibold text-amber-600">Highly Rated on Chewy</span>
            </div>
            <h4 className="font-bold text-gray-800 mb-1.5 line-clamp-2 text-sm leading-snug min-h-[40px]">
              {product.name}
            </h4>
            {product.whyWeLoveIt && (
              <p className="text-xs text-gray-500 mb-3 line-clamp-2 italic bg-gray-50 px-2 py-1 rounded-lg">
                &ldquo;{product.whyWeLoveIt}&rdquo;
              </p>
            )}
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-lg font-bold text-teal-600">${product.price}</span>
              {product.originalPrice && (
                <span className="text-xs text-gray-400 line-through">${product.originalPrice}</span>
              )}
            </div>
            <button className="w-full bg-teal-500 hover:bg-teal-600 text-white font-bold py-2.5 px-4 rounded-full flex items-center justify-center gap-2 transition-colors whitespace-nowrap min-h-[44px] text-sm">
              <i className={getRetailerIcon(product.retailer)}></i>
              Shop on {getRetailerName(product.retailer)}
            </button>
          </div>
        </div>
      ))}
    </div>
    <div className="flex justify-center gap-1.5 mt-1">
      {items.map((_, index) => (
        <div key={index} className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
      ))}
    </div>
  </div>
);

export default EditorPicksCarousel;